import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { authService } from '../../services/authService';
import {
  Box,
  CircularProgress,
  IconButton,
  Typography,
  Paper,
  Button,
  Tooltip,
  Fab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Alert,
} from '@mui/material';
import {
  ZoomIn,
  ZoomOut,
  NavigateBefore,
  NavigateNext,
  Save,
  Add,
  Delete,
  Edit,
  DragIndicator,
} from '@mui/icons-material';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { FieldMappingItem } from '../../models';
import FieldEditDialog from './FieldEditDialog';

// PDF.js worker 설정
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

interface DraggableFieldProps {
  field: FieldMappingItem;
  containerWidth: number;
  containerHeight: number;
  isSelected: boolean;
  onSelect: () => void;
  onPositionChange: (position: { top: number; left: number; width: number; height: number }) => void;
  onDelete: () => void;
  onEdit: () => void;
}

const DraggableField: React.FC<DraggableFieldProps> = ({
  field,
  containerWidth,
  containerHeight,
  isSelected,
  onSelect,
  onPositionChange,
  onDelete,
  onEdit,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [initialPos, setInitialPos] = useState({ top: 0, left: 0, width: 0, height: 0 });
  const fieldRef = useRef<HTMLDivElement>(null);

  const position = field.position || { top: 10, left: 10, width: 20, height: 3 };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect();
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    setInitialPos(position);
  };

  const handleResizeMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect();
    setIsResizing(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    setInitialPos(position);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging && !isResizing) return;

      const deltaX = ((e.clientX - dragStart.x) / containerWidth) * 100;
      const deltaY = ((e.clientY - dragStart.y) / containerHeight) * 100;

      if (isDragging) {
        const newTop = Math.max(0, Math.min(100 - initialPos.height, initialPos.top + deltaY));
        const newLeft = Math.max(0, Math.min(100 - initialPos.width, initialPos.left + deltaX));
        onPositionChange({
          ...initialPos,
          top: newTop,
          left: newLeft,
        });
      } else if (isResizing) {
        const newWidth = Math.max(5, Math.min(100 - initialPos.left, initialPos.width + deltaX));
        const newHeight = Math.max(2, Math.min(100 - initialPos.top, initialPos.height + deltaY));
        onPositionChange({
          ...initialPos,
          width: newWidth,
          height: newHeight,
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, dragStart, initialPos, containerWidth, containerHeight, onPositionChange]);

  return (
    <Box
      ref={fieldRef}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      sx={{
        position: 'absolute',
        top: `${position.top}%`,
        left: `${position.left}%`,
        width: `${position.width}%`,
        height: `${position.height}%`,
        border: isSelected ? '2px solid #1976d2' : '1px solid rgba(25, 118, 210, 0.5)',
        borderRadius: '4px',
        backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.2)' : 'rgba(25, 118, 210, 0.1)',
        cursor: isDragging ? 'grabbing' : 'grab',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        userSelect: 'none',
        zIndex: isSelected ? 100 : 10,
        '&:hover': {
          backgroundColor: 'rgba(25, 118, 210, 0.2)',
        },
      }}
      onMouseDown={handleMouseDown}
    >
      <Typography
        variant="caption"
        sx={{
          color: '#1976d2',
          fontWeight: isSelected ? 600 : 400,
          fontSize: '10px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          px: 0.5,
        }}
      >
        {field.label}
      </Typography>

      {/* 리사이즈 핸들 */}
      {isSelected && (
        <Box
          sx={{
            position: 'absolute',
            right: -4,
            bottom: -4,
            width: 8,
            height: 8,
            backgroundColor: '#1976d2',
            cursor: 'se-resize',
            borderRadius: '50%',
          }}
          onMouseDown={handleResizeMouseDown}
        />
      )}

      {/* 선택 시 툴바 */}
      {isSelected && (
        <Box
          sx={{
            position: 'absolute',
            top: -32,
            right: 0,
            display: 'flex',
            gap: 0.5,
            backgroundColor: 'white',
            borderRadius: 1,
            boxShadow: 1,
            p: 0.25,
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <IconButton size="small" onClick={onEdit} title="편집">
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={onDelete} color="error" title="삭제">
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

interface PDFFieldMappingEditorProps {
  pdfUrl: string;
  initialFields: FieldMappingItem[];
  onSave: (fields: FieldMappingItem[]) => void;
  onCancel?: () => void;
  width?: number;
}

export const PDFFieldMappingEditor: React.FC<PDFFieldMappingEditorProps> = ({
  pdfUrl,
  initialFields,
  onSave,
  onCancel,
  width = 700,
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pageSize, setPageSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });
  const [fields, setFields] = useState<FieldMappingItem[]>(initialFields);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState<FieldMappingItem | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // PDF 파일 객체 (인증 헤더 포함)
  const pdfFile = useMemo(() => {
    const token = authService.getAccessToken();
    if (!token) return pdfUrl;
    return {
      url: pdfUrl,
      httpHeaders: {
        Authorization: `Bearer ${token}`,
      },
    };
  }, [pdfUrl]);

  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setLoading(false);
    setError(null);
  }, []);

  const onDocumentLoadError = useCallback((error: Error) => {
    setLoading(false);
    setError(`PDF 로딩 실패: ${error.message}`);
  }, []);

  const onPageLoadSuccess = useCallback(
    (page: { width: number; height: number; originalWidth?: number; originalHeight?: number }) => {
      // react-pdf Page의 onLoadSuccess는 렌더링된 크기(width, height)와 원본 크기를 반환
      // originalWidth/originalHeight가 있으면 원본 비율 계산에 사용
      const origWidth = page.originalWidth || page.width;
      const origHeight = page.originalHeight || page.height;
      setPageSize({ width: origWidth, height: origHeight });
    },
    []
  );

  const changePage = useCallback(
    (offset: number) => {
      const newPage = pageNumber + offset;
      if (newPage >= 1 && newPage <= numPages) {
        setPageNumber(newPage);
      }
    },
    [pageNumber, numPages]
  );

  const zoomIn = useCallback(() => {
    setScale((prev) => Math.min(prev + 0.25, 2.5));
  }, []);

  const zoomOut = useCallback(() => {
    setScale((prev) => Math.max(prev - 0.25, 0.5));
  }, []);

  // 실제 렌더링되는 PDF 크기 계산
  const scaledWidth = width * scale;
  // pageSize는 원본 PDF 크기, 이를 기반으로 스케일된 높이 계산
  const aspectRatio = pageSize.height / pageSize.width || 1.414; // A4 기본 비율
  const scaledHeight = scaledWidth * aspectRatio;

  const handleAddField = () => {
    const newField: FieldMappingItem = {
      field_id: `field_${Date.now()}`,
      hwpx_field: '',
      label: '새 필드',
      type: 'text',
      position: {
        top: 20,
        left: 20,
        width: 25,
        height: 4,
      },
    };
    setEditingField(newField);
    setEditDialogOpen(true);
  };

  const handleFieldPositionChange = (fieldId: string, position: { top: number; left: number; width: number; height: number }) => {
    setFields((prev) =>
      prev.map((f) => (f.field_id === fieldId ? { ...f, position } : f))
    );
  };

  const handleDeleteField = (fieldId: string) => {
    setFields((prev) => prev.filter((f) => f.field_id !== fieldId));
    if (selectedFieldId === fieldId) {
      setSelectedFieldId(null);
    }
  };

  const handleEditField = (field: FieldMappingItem) => {
    setEditingField(field);
    setEditDialogOpen(true);
  };

  const handleSaveField = (field: FieldMappingItem) => {
    setFields((prev) => {
      const existingIndex = prev.findIndex((f) => f.field_id === field.field_id);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = field;
        return updated;
      }
      return [...prev, field];
    });
    setEditDialogOpen(false);
    setEditingField(null);
  };

  const handleSave = () => {
    onSave(fields);
  };

  const handleContainerClick = () => {
    setSelectedFieldId(null);
  };

  return (
    <Box sx={{ display: 'flex', gap: 2 }}>
      {/* PDF 영역 */}
      <Paper elevation={2} sx={{ flex: 1, p: 2 }}>
        {/* 컨트롤 바 */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
            p: 1,
            bgcolor: 'grey.100',
            borderRadius: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={() => changePage(-1)} disabled={pageNumber <= 1} size="small">
              <NavigateBefore />
            </IconButton>
            <Typography variant="body2">
              {pageNumber} / {numPages}
            </Typography>
            <IconButton onClick={() => changePage(1)} disabled={pageNumber >= numPages} size="small">
              <NavigateNext />
            </IconButton>
            <Box sx={{ mx: 1, borderLeft: '1px solid', borderColor: 'grey.300', height: 24 }} />
            <IconButton onClick={zoomOut} disabled={scale <= 0.5} size="small">
              <ZoomOut />
            </IconButton>
            <Typography variant="body2" sx={{ minWidth: 50, textAlign: 'center' }}>
              {Math.round(scale * 100)}%
            </Typography>
            <IconButton onClick={zoomIn} disabled={scale >= 2.5} size="small">
              <ZoomIn />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {onCancel && (
              <Button variant="outlined" size="small" onClick={onCancel}>
                취소
              </Button>
            )}
            <Button
              variant="contained"
              size="small"
              startIcon={<Save />}
              onClick={handleSave}
            >
              저장
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* PDF 및 필드 오버레이 */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            overflow: 'auto',
            maxHeight: 'calc(100vh - 300px)',
          }}
        >
          <Box
            ref={containerRef}
            sx={{
              position: 'relative',
              width: scaledWidth,
              height: scaledHeight,
              border: '1px solid',
              borderColor: 'grey.300',
              bgcolor: 'white',
              boxShadow: 2,
            }}
            onClick={handleContainerClick}
          >
            {loading && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: scaledWidth,
                  height: 400,
                }}
              >
                <CircularProgress />
              </Box>
            )}
            <Document
              file={pdfFile}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={null}
              error={null}
            >
              <Page
                pageNumber={pageNumber}
                width={scaledWidth}
                onLoadSuccess={onPageLoadSuccess}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </Document>

            {/* 드래그 가능한 필드 오버레이 */}
            {!loading &&
              fields.map((field) => (
                <DraggableField
                  key={field.field_id}
                  field={field}
                  containerWidth={scaledWidth}
                  containerHeight={scaledHeight}
                  isSelected={selectedFieldId === field.field_id}
                  onSelect={() => setSelectedFieldId(field.field_id)}
                  onPositionChange={(position) => handleFieldPositionChange(field.field_id, position)}
                  onDelete={() => handleDeleteField(field.field_id)}
                  onEdit={() => handleEditField(field)}
                />
              ))}
          </Box>
        </Box>
      </Paper>

      {/* 필드 목록 패널 */}
      <Paper elevation={2} sx={{ width: 280, p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">필드 목록</Typography>
          <Tooltip title="필드 추가">
            <Fab color="primary" size="small" onClick={handleAddField}>
              <Add />
            </Fab>
          </Tooltip>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {fields.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            필드가 없습니다.
            <br />
            '+' 버튼을 눌러 필드를 추가하세요.
          </Typography>
        ) : (
          <List dense>
            {fields.map((field) => (
              <ListItem
                key={field.field_id}
                button
                selected={selectedFieldId === field.field_id}
                onClick={() => setSelectedFieldId(field.field_id)}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  '&.Mui-selected': {
                    backgroundColor: 'primary.light',
                    color: 'primary.contrastText',
                  },
                }}
              >
                <DragIndicator sx={{ mr: 1, color: 'grey.400' }} />
                <ListItemText
                  primary={field.label}
                  secondary={`${field.type}${field.required ? ' *' : ''}`}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
                <ListItemSecondaryAction>
                  <IconButton size="small" onClick={() => handleEditField(field)}>
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDeleteField(field.field_id)} color="error">
                    <Delete fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      {/* 필드 편집 다이얼로그 */}
      <FieldEditDialog
        open={editDialogOpen}
        field={editingField}
        onSave={handleSaveField}
        onClose={() => {
          setEditDialogOpen(false);
          setEditingField(null);
        }}
      />
    </Box>
  );
};

export default PDFFieldMappingEditor;
